import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { DataGridPro, type GridCellParams, type GridColDef } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import {
  SaveSlotTemplatesDocument,
  SiteDocument,
  SlotTemplatesDocument,
  Weekday,
} from '../gql/graphql'
import { useRole } from '../hooks'

gql`
  query SlotTemplates($siteId: UUID!) {
    slotTemplates(siteId: $siteId) {
      courtId
      weekday
      hour
      price
    }
  }

  mutation SaveSlotTemplates($siteId: ID!, $slots: [SlotTemplateSlotInput!]!) {
    saveSlotTemplates(siteId: $siteId, slots: $slots)
  }
`

const WEEKDAYS = [
  { label: '周一', value: Weekday.Monday },
  { label: '周二', value: Weekday.Tuesday },
  { label: '周三', value: Weekday.Wednesday },
  { label: '周四', value: Weekday.Thursday },
  { label: '周五', value: Weekday.Friday },
  { label: '周六', value: Weekday.Saturday },
  { label: '周日', value: Weekday.Sunday },
] as const

const HOURS = Array.from({ length: 24 }, (_, i) => i)

function formatHour(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`
}

/** Build a modification map key (use :: separator to avoid UUID hyphen conflicts) */
function modKey(weekday: Weekday, hour: number, courtId: string) {
  return `${weekday}::${hour}::${courtId}`
}

function parseModKey(key: string) {
  const [weekday, hourStr, courtId] = key.split('::')
  return { weekday: weekday as Weekday, hour: Number(hourStr), courtId }
}

/** Extract {hour, courtId} from a mouse event target within the DataGrid */
function getCellInfo(target: HTMLElement): { hour: number; courtId: string } | null {
  const cell = target.closest('[data-field]') as HTMLElement | null
  const row = target.closest('[data-id]') as HTMLElement | null
  if (!cell || !row) return null
  const field = cell.dataset.field
  const rowId = row.dataset.id
  if (!field || field === 'hour' || rowId == null) return null
  return { hour: Number(rowId), courtId: field }
}

/** Compute the rectangle of cells between two corners */
function getCellRect(
  start: { hour: number; courtId: string },
  end: { hour: number; courtId: string },
  courtIds: string[],
) {
  const minHour = Math.min(start.hour, end.hour)
  const maxHour = Math.max(start.hour, end.hour)
  const si = courtIds.indexOf(start.courtId)
  const ei = courtIds.indexOf(end.courtId)
  if (si === -1 || ei === -1) return []
  const minCol = Math.min(si, ei)
  const maxCol = Math.max(si, ei)
  const cells: { hour: number; courtId: string }[] = []
  for (let h = minHour; h <= maxHour; h++) {
    for (let c = minCol; c <= maxCol; c++) {
      cells.push({ hour: h, courtId: courtIds[c] })
    }
  }
  return cells
}

const HIGHLIGHT_CLASS = 'cell-drag-selected'

export default function SlotTemplatePage() {
  const { currentSite } = useRole()
  const [weekdayTab, setWeekdayTab] = useState(0)
  const selectedWeekday = WEEKDAYS[weekdayTab].value

  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useQuery(SiteDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'cache-first',
  })

  const {
    data: templatesData,
    loading: templatesLoading,
    error: templatesError,
  } = useQuery(SlotTemplatesDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })

  const [saveSlotTemplates, { loading: saving }] = useMutation(
    SaveSlotTemplatesDocument,
  )

  // Modification tracking: key -> price string, or null for unavailable
  const [modifications, setModifications] = useState<Map<string, string | null>>(
    new Map(),
  )
  const isModified = modifications.size > 0

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedCells, setSelectedCells] = useState<
    { hour: number; courtId: string }[]
  >([])
  const [priceInput, setPriceInput] = useState('')
  const [markUnavailable, setMarkUnavailable] = useState(false)

  // Navigation blocker state
  const [showNavigationDialog, setShowNavigationDialog] = useState(false)

  // Copy template state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [copyTargetWeekdays, setCopyTargetWeekdays] = useState<Weekday[]>([])
  const [copyWarningOpen, setCopyWarningOpen] = useState(false)

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isModified && currentLocation.pathname !== nextLocation.pathname,
  )

  // Show dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowNavigationDialog(true)
    }
  }, [blocker.state])

  // Drag selection refs (use refs to avoid re-renders during drag)
  const gridRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const dragStart = useRef<{ hour: number; courtId: string } | null>(null)
  const priceInputRef = useRef<HTMLInputElement>(null)

  const courts = useMemo(() => {
    const courtsList = sitesData?.site.courts ?? []
    return [...courtsList].sort((a, b) => a.displayOrder - b.displayOrder)
  }, [sitesData])

  const courtIds = useMemo(() => courts.map((c) => c.id), [courts])

  // Focus price input when dialog opens
  useEffect(() => {
    if (dialogOpen && !markUnavailable) {
      // Small delay to ensure dialog animation completes
      const timer = setTimeout(() => {
        priceInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [dialogOpen, markUnavailable])

  const columns = useMemo<GridColDef[]>(() => {
    const hourCol: GridColDef = {
      field: 'hour',
      headerName: '时段',
      width: 80,
      sortable: false,
      pinnable: false,
      align: 'center',
      headerAlign: 'center',
    }
    const courtCols: GridColDef[] = courts.map((court) => ({
      field: court.id,
      headerName: court.name,
      width: 100,
      sortable: false,
      pinnable: false,
      align: 'center',
      headerAlign: 'center',
    }))
    return [hourCol, ...courtCols]
  }, [courts])

  const rows = useMemo(() => {
    const templates = templatesData?.slotTemplates ?? []
    const filtered = templates.filter((t) => t.weekday === selectedWeekday)
    const lookup = new Map<string, string>()
    for (const t of filtered) {
      lookup.set(`${t.hour}-${t.courtId}`, t.price)
    }
    return HOURS.map((hour) => {
      const row: Record<string, string | number> = {
        id: hour,
        hour: formatHour(hour),
      }
      for (const court of courts) {
        const mk = modKey(selectedWeekday, hour, court.id)
        if (modifications.has(mk)) {
          const mod = modifications.get(mk)
          row[court.id] = mod != null ? `¥${mod}` : '—'
        } else {
          const orig = lookup.get(`${hour}-${court.id}`)
          row[court.id] = orig != null ? `¥${orig}` : '—'
        }
      }
      return row
    })
  }, [templatesData, selectedWeekday, courts, modifications])

  // --- Drag selection ---
  const clearHighlight = useCallback(() => {
    gridRef.current?.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
      el.classList.remove(HIGHLIGHT_CLASS)
    })
  }, [])

  const highlightRect = useCallback(
    (
      start: { hour: number; courtId: string },
      end: { hour: number; courtId: string },
    ) => {
      clearHighlight()
      for (const { hour, courtId } of getCellRect(start, end, courtIds)) {
        const row = gridRef.current?.querySelector(`[data-id="${hour}"]`)
        row?.querySelector(`[data-field="${courtId}"]`)?.classList.add(HIGHLIGHT_CLASS)
      }
    },
    [courtIds, clearHighlight],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const info = getCellInfo(e.target as HTMLElement)
      if (!info) return
      isDragging.current = true
      dragStart.current = info
      clearHighlight()
      highlightRect(info, info)
      e.preventDefault()
    },
    [clearHighlight, highlightRect],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current || !dragStart.current) return
      const info = getCellInfo(e.target as HTMLElement)
      if (!info) return
      highlightRect(dragStart.current, info)
    },
    [highlightRect],
  )

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current || !dragStart.current) return
      isDragging.current = false
      const end = getCellInfo(e.target as HTMLElement) || dragStart.current
      const cells = getCellRect(dragStart.current, end, courtIds)
      dragStart.current = null
      clearHighlight()
      if (cells.length === 0) return
      // The DataGrid cell under the cursor retains DOM focus after mouseup. Blur it
      // before opening the dialog so MUI's aria-hidden on the root doesn't land on
      // a still-focused descendant.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      setSelectedCells(cells)
      setPriceInput('')
      setMarkUnavailable(false)
      setDialogOpen(true)
    },
    [courtIds, clearHighlight],
  )

  // --- Dialog submit ---
  const handleDialogSubmit = useCallback(() => {
    if (!markUnavailable && priceInput === '') return
    setModifications((prev) => {
      const next = new Map(prev)
      const formattedPrice = markUnavailable ? null : Number(priceInput).toFixed(2)
      for (const { hour, courtId } of selectedCells) {
        const key = modKey(selectedWeekday, hour, courtId)
        next.set(key, formattedPrice)
      }
      return next
    })
    setDialogOpen(false)
  }, [selectedCells, selectedWeekday, markUnavailable, priceInput])

  // --- Save ---
  const handleSave = useCallback(async () => {
    if (!currentSite) return
    const templates = templatesData?.slotTemplates ?? []
    // Build map of all original templates
    const allSlots = new Map<
      string,
      { courtId: string; weekday: Weekday; hour: number; price: string }
    >()
    for (const t of templates) {
      allSlots.set(modKey(t.weekday, t.hour, t.courtId), {
        courtId: t.courtId,
        weekday: t.weekday,
        hour: t.hour,
        price: t.price,
      })
    }
    // Apply modifications
    for (const [key, value] of modifications) {
      if (value == null) {
        allSlots.delete(key)
      } else {
        const parsed = parseModKey(key)
        allSlots.set(key, { ...parsed, price: value })
      }
    }
    const slots = Array.from(allSlots.values()).map((s) => ({
      courtId: s.courtId,
      weekday: s.weekday,
      hour: s.hour,
      price: s.price,
    }))
    try {
      await saveSlotTemplates({
        variables: { siteId: currentSite.id, slots },
        update: (cache) => {
          // Write the updated slots directly to the cache
          cache.writeQuery({
            query: SlotTemplatesDocument,
            variables: { siteId: currentSite.id },
            data: {
              __typename: 'Query' as const,
              slotTemplates: slots.map((s) => ({
                __typename: 'SlotTemplate' as const,
                ...s,
              })),
            },
          })
        },
      })
      message.success('保存成功')
      setModifications(new Map())
    } catch (err) {
      message.error(`保存失败: ${err instanceof Error ? err.message : err}`)
    }
  }, [currentSite, templatesData, modifications, saveSlotTemplates])

  // --- Reset ---
  const handleReset = () => {
    setModifications(new Map())
  }

  // --- Navigation dialog handlers ---
  const handleNavigationSaveAndLeave = useCallback(async () => {
    if (!currentSite) return
    const templates = templatesData?.slotTemplates ?? []
    const allSlots = new Map<
      string,
      { courtId: string; weekday: Weekday; hour: number; price: string }
    >()
    for (const t of templates) {
      allSlots.set(modKey(t.weekday, t.hour, t.courtId), {
        courtId: t.courtId,
        weekday: t.weekday,
        hour: t.hour,
        price: t.price,
      })
    }
    for (const [key, value] of modifications) {
      if (value == null) {
        allSlots.delete(key)
      } else {
        const parsed = parseModKey(key)
        allSlots.set(key, { ...parsed, price: value })
      }
    }
    const slots = Array.from(allSlots.values()).map((s) => ({
      courtId: s.courtId,
      weekday: s.weekday,
      hour: s.hour,
      price: s.price,
    }))
    try {
      await saveSlotTemplates({
        variables: { siteId: currentSite.id, slots },
      })
      message.success('保存成功')
      setModifications(new Map())
      setShowNavigationDialog(false)
      blocker.proceed?.()
    } catch (err) {
      message.error(`保存失败: ${err instanceof Error ? err.message : err}`)
    }
  }, [currentSite, templatesData, modifications, saveSlotTemplates, blocker])

  const handleNavigationLeaveWithoutSaving = useCallback(() => {
    setModifications(new Map())
    setShowNavigationDialog(false)
    blocker.proceed?.()
  }, [blocker])

  const handleNavigationCancel = useCallback(() => {
    setShowNavigationDialog(false)
    blocker.reset?.()
  }, [blocker])

  // Check if current weekday has any template data defined
  const currentWeekdayHasData = (templatesData?.slotTemplates ?? []).some(
    (t) => t.weekday === selectedWeekday,
  )

  // --- Copy template handlers ---
  const handleCopyClick = () => {
    setCopyTargetWeekdays([])
    setCopyDialogOpen(true)
  }

  const handleCopyConfirm = () => {
    setCopyDialogOpen(false)
    setCopyWarningOpen(true)
  }

  const handleCopyWarningConfirm = useCallback(() => {
    const templates = templatesData?.slotTemplates ?? []
    setModifications((prev) => {
      const next = new Map(prev)
      for (const targetWeekday of copyTargetWeekdays) {
        for (const court of courts) {
          for (const hour of HOURS) {
            const sourceKey = modKey(selectedWeekday, hour, court.id)
            const targetKey = modKey(targetWeekday, hour, court.id)

            // Resolve effective source value
            const sourceValue = prev.has(sourceKey)
              ? (prev.get(sourceKey) as string | null)
              : (templates.find(
                  (t) =>
                    t.weekday === selectedWeekday &&
                    t.hour === hour &&
                    t.courtId === court.id,
                )?.price ?? null)

            // Resolve original (pre-modification) target value
            const originalTargetValue =
              templates.find(
                (t) =>
                  t.weekday === targetWeekday &&
                  t.hour === hour &&
                  t.courtId === court.id,
              )?.price ?? null

            if (sourceValue === originalTargetValue) {
              // Source matches original target — no change needed, remove any stale modification
              next.delete(targetKey)
            } else {
              next.set(targetKey, sourceValue)
            }
          }
        }
      }
      return next
    })
    message.success('复制成功')
    setCopyWarningOpen(false)
  }, [copyTargetWeekdays, templatesData, selectedWeekday, courts])

  const loading = sitesLoading || templatesLoading
  const error = sitesError || templatesError

  const priceValid =
    markUnavailable ||
    (priceInput !== '' && Number(priceInput) >= 0 && Number(priceInput) <= 999)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography variant="h4">时段模板</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentWeekdayHasData && (
            <Button variant="outlined" onClick={handleCopyClick}>
              复制到其他日期
            </Button>
          )}
          {isModified && (
            <>
              <Button variant="outlined" onClick={handleReset} disabled={saving}>
                重置
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? '保存中...' : '保存'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">加载时段模板失败: {error.message}</Alert>}

      {!loading && !error && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Tabs value={weekdayTab} onChange={(_, v) => setWeekdayTab(v)}>
              {WEEKDAYS.map((day) => (
                <Tab key={day.value} label={day.label} />
              ))}
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {[
                {
                  color: 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(0,0,0,0.12)',
                  label: '不可用',
                },
                {
                  color: 'rgba(25, 118, 210, 0.08)',
                  border: '1px solid rgba(25,118,210,0.3)',
                  label: '已修改',
                },
                {
                  color: '#fff',
                  border: '1px solid rgba(0,0,0,0.12)',
                  label: '未修改',
                },
              ].map(({ color, border, label }) => (
                <Box
                  key={label}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: color,
                      border,
                      borderRadius: '2px',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            ref={gridRef}
            sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <DataGridPro
              rows={rows}
              columns={columns}
              hideFooter
              disableRowSelectionOnClick
              disableColumnMenu
              density="compact"
              getCellClassName={(
                params: GridCellParams<Record<string, string | number>>,
              ) => {
                if (params.field === 'hour') return ''
                const mk = modKey(
                  selectedWeekday,
                  params.row.id as number,
                  params.field,
                )
                const isModifiedCell = modifications.has(mk)
                const isUnavail = params.value === '—'
                return [
                  isUnavail ? 'unavailable-cell' : '',
                  isModifiedCell ? 'modified-cell' : '',
                ]
                  .filter(Boolean)
                  .join(' ')
              }}
              sx={{
                height: '100%',
                userSelect: 'none',
                '& .MuiDataGrid-cell': {
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  cursor: 'cell',
                },
                '& .MuiDataGrid-columnHeader': {
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .unavailable-cell': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  color: 'rgba(0, 0, 0, 0.38)',
                },
                '& .modified-cell': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
                [`& .${HIGHLIGHT_CLASS}`]: {
                  backgroundColor: 'rgba(25, 118, 210, 0.16) !important',
                },
              }}
              initialState={{
                pinnedColumns: { left: ['hour'] },
              }}
            />
          </Box>
        </>
      )}

      {/* Price edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>设置价格</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            已选择 {selectedCells.length} 个时段
          </Typography>
          <TextField
            label="价格 (元)"
            type="number"
            fullWidth
            inputRef={priceInputRef}
            value={priceInput}
            onChange={(e) => {
              const v = e.target.value
              // Reject any input containing minus sign
              if (v.includes('-')) return
              if (v === '' || (/^\d{0,3}(\.\d{0,2})?$/.test(v) && Number(v) <= 999)) {
                setPriceInput(v)
              }
            }}
            onKeyDown={(e) => {
              // Prevent minus/dash key
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault()
              }
            }}
            disabled={markUnavailable}
            slotProps={{
              htmlInput: { min: 0, max: 999, step: 0.01 },
            }}
            sx={{
              mb: 2,
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={markUnavailable}
                onChange={(e) => setMarkUnavailable(e.target.checked)}
              />
            }
            label="设为不可用"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            disabled={!priceValid}
          >
            确定
          </Button>
        </DialogActions>
      </Dialog>

      {/* Navigation confirmation dialog */}
      <Dialog
        open={showNavigationDialog}
        onClose={handleNavigationCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>未保存的更改</DialogTitle>
        <DialogContent>
          <Typography>您有未保存的更改。是否要在离开前保存这些更改？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNavigationCancel}>取消</Button>
          <Button onClick={handleNavigationLeaveWithoutSaving} color="error">
            不保存
          </Button>
          <Button
            variant="contained"
            onClick={handleNavigationSaveAndLeave}
            disabled={saving}
          >
            保存并离开
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy template dialog */}
      <Dialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>复制时段模板</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            将{WEEKDAYS[weekdayTab].label}的时段模板复制到：
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
            {WEEKDAYS.filter((day) => day.value !== selectedWeekday).map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    checked={copyTargetWeekdays.includes(day.value)}
                    onChange={(e) => {
                      setCopyTargetWeekdays((prev) =>
                        e.target.checked
                          ? [...prev, day.value]
                          : prev.filter((v) => v !== day.value),
                      )
                    }}
                  />
                }
                label={day.label}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleCopyConfirm}
            disabled={copyTargetWeekdays.length === 0}
          >
            确定
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy warning dialog */}
      <Dialog
        open={copyWarningOpen}
        onClose={() => setCopyWarningOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>确认覆盖</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {copyTargetWeekdays.length > 0 &&
              `${copyTargetWeekdays.map((v) => WEEKDAYS.find((d) => d.value === v)?.label).join('、')}的时段模板将被覆盖。确定要继续吗？`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyWarningOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCopyWarningConfirm}
          >
            确认覆盖
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
